import { useMemo, useState } from 'react';

import DocumentsStepIcon from 'src/assets/step-icon-documents.svg?react';
import { useProductTypeCustomerLeadSpecification } from 'src/hooks/useProductTypeCustomerLeadSpecification';
import {
  CustomerPageStep,
  type ICustomerStepInfo,
  type ICustomersContext,
} from 'src/pages/CustomersPage/CustomersPage.context.types';
import type { ICustomerLeadProduct } from 'src/types/customers/ICustomerLeadProduct';
import { ProductSpecificationFieldGroup } from 'src/types/reference/ProductSpecificationFieldGroup';

export function useCustomersContextCreator() {
  const [isEditing, setIsEditing] = useState(false);

  const [isMobileGuideOpened, setIsMobileGuideOpened] = useState(false);

  const [isLastCustomerStep, setIsLastCustomerStep] = useState(false);

  const [customerStep, setCustomerStep] = useState<CustomerPageStep>(CustomerPageStep.Start);
  const [customerStepGuideKey, setCustomerStepGuideKey] = useState<string | null>(null);

  const [customerStepsInfo] = useState<ICustomerStepInfo[]>([
    {
      step: CustomerPageStep.Quantities,
      description: 'Quantities',
      icon: <DocumentsStepIcon />,
    },
    {
      step: CustomerPageStep.ProductSpecifications,
      description: 'Product Specifications',
      icon: <DocumentsStepIcon />,
    },
    {
      step: CustomerPageStep.AdditionalInformation,
      description: 'Additional Customer Information',
      icon: <DocumentsStepIcon />,
    },
    {
      step: CustomerPageStep.ShippingAndContacts,
      description: 'Shipping & Contacts',
      icon: <DocumentsStepIcon />,
    },
  ]);

  const [customerLeadProduct, setCustomerLeadProduct] = useState<ICustomerLeadProduct | null>(null);

  const customerLeadProductSpecification = useProductTypeCustomerLeadSpecification(customerLeadProduct?.productTypeId);

  const customersContext = useMemo<ICustomersContext>(
    () => ({
      isEditing,

      isMobileGuideOpened,

      isLastCustomerStep,

      customerStep,
      customerStepsInfo,
      customerStepGuideKey,

      customerLeadProduct,
      customerLeadProductSpecification,

      onEditing: (newIsEditing: boolean) => setIsEditing(newIsEditing),

      toggleMobileGuideOpened: () => {
        setIsMobileGuideOpened((prev) => !prev);
      },

      goNextStep: () => {
        const newStep =
          customerStep === CustomerPageStep.Start
            ? CustomerPageStep.Quantities
            : customerStep === CustomerPageStep.Quantities
              ? CustomerPageStep.ProductSpecifications
              : customerStep === CustomerPageStep.ProductSpecifications
                ? CustomerPageStep.AdditionalInformation
                : customerStep === CustomerPageStep.AdditionalInformation
                  ? CustomerPageStep.ShippingAndContacts
                  : CustomerPageStep.Done;

        setCustomerStep(newStep);

        setIsLastCustomerStep(false);

        const newStepGuideKey =
          newStep === CustomerPageStep.Quantities
            ? ProductSpecificationFieldGroup.Quantities
            : newStep === CustomerPageStep.ProductSpecifications
              ? ProductSpecificationFieldGroup.ProductDetails
              : newStep === CustomerPageStep.ShippingAndContacts
                ? ProductSpecificationFieldGroup.Contacts
                : null;

        setCustomerStepGuideKey(newStepGuideKey);

        setIsEditing(false);
      },

      goBack: () => {
        const newStep =
          customerStep === CustomerPageStep.Done
            ? CustomerPageStep.ShippingAndContacts
            : customerStep === CustomerPageStep.ShippingAndContacts
              ? CustomerPageStep.AdditionalInformation
              : customerStep === CustomerPageStep.AdditionalInformation
                ? CustomerPageStep.ProductSpecifications
                : customerStep === CustomerPageStep.ProductSpecifications
                  ? CustomerPageStep.Quantities
                  : CustomerPageStep.Start;

        setCustomerStep(newStep);

        setIsLastCustomerStep(false);

        const newStepGuideKey =
          newStep === CustomerPageStep.Quantities
            ? ProductSpecificationFieldGroup.Quantities
            : newStep === CustomerPageStep.ProductSpecifications
              ? ProductSpecificationFieldGroup.ProductDetails
              : newStep === CustomerPageStep.ShippingAndContacts
                ? ProductSpecificationFieldGroup.Contacts
                : null;

        setCustomerStepGuideKey(newStepGuideKey);

        setIsEditing(false);
      },

      setLastCustomerStep: (newIsLastCustomerStep: boolean) => setIsLastCustomerStep(newIsLastCustomerStep),

      setCustomerStep: (newCustomerStep: CustomerPageStep) => {
        setCustomerStep(newCustomerStep);

        setIsLastCustomerStep(false);

        const newStepGuideKey =
          newCustomerStep === CustomerPageStep.Quantities
            ? ProductSpecificationFieldGroup.Quantities
            : newCustomerStep === CustomerPageStep.ProductSpecifications
              ? ProductSpecificationFieldGroup.ProductDetails
              : newCustomerStep === CustomerPageStep.ShippingAndContacts
                ? ProductSpecificationFieldGroup.Contacts
                : null;

        setCustomerStepGuideKey(newStepGuideKey);

        setIsEditing(false);
      },
      setCustomerStepGuideKey: (newCustomerStepGuide: string | null) => setCustomerStepGuideKey(newCustomerStepGuide),

      updateCustomerLeadProduct: (newCustomerProductLead: ICustomerLeadProduct) =>
        setCustomerLeadProduct((prev) => ({
          ...(prev ?? {}),

          ...newCustomerProductLead,
        })),
    }),
    [
      isEditing,
      isMobileGuideOpened,
      isLastCustomerStep,
      customerStep,
      customerStepsInfo,
      customerStepGuideKey,
      customerLeadProduct,
      customerLeadProductSpecification,
    ]
  );

  return customersContext;
}
